/**
 * ClarificationService - Phase 1.2: Conversational Clarification System
 * Enhanced with Phase 4.1: Chart-Specific Clarifications
 *
 * Detects ambiguous commands and generates clarification questions.
 * Enables conversational interactions like:
 * - "make the header bigger" -> "I found 3 components that could be 'header'. Which one?"
 * - "make it bigger" -> "How much bigger? [32px] [48px] [Double it] [Custom]"
 * - "use theme" -> "Which theme? [Agricultural] [Blue] [Rainbow] [Sunset]"
 * - "convert chart" -> "Which type? [Bar] [Line] [Pie] [Area]"
 */

import { v4 as uuidv4 } from 'uuid';
import { Component, ComponentType } from '@/types';
import { useEditorStore } from '@/stores/editor/editor-store';
import { logger } from '@/utils/logger';
import { componentReferenceResolver } from './ComponentReferenceResolver';
import { chartTypeRegistry } from './charts/ChartTypeRegistry';
import type { BaseComponentProperties } from '@/types/component-properties';
import type {
  ClarificationRequest,
  ClarificationOption,
  ClarificationResponse,
  AmbiguityDetectionResult,
  MatchedComponent,
  ValueSuggestion,
  PropertyChangePreview,
  ClarificationServiceConfig,
} from '@/types/ai';

/**
 * Default configuration for the clarification service
 */
const DEFAULT_CONFIG: ClarificationServiceConfig = {
  maxOptionsToShow: 4,
  enableComponentMatching: true,
  enableValueSuggestions: true,
  enableBreakpointClarification: true,
  confidenceThreshold: 0.7,
};

/**
 * Ambiguity detection patterns organized by type
 */
const AMBIGUITY_PATTERNS = {
  // Component reference patterns - may match multiple components
  componentReference: {
    patterns: [
      /(?:the|this|that)\s+(\w+(?:\s+\w+)?)/i,     // "the header", "the submit button"
      /make\s+(\w+)\s+/i,                           // "make title bigger"
      /change\s+(\w+)\s+/i,                         // "change button color"
      /update\s+(\w+)/i,                            // "update sidebar"
      /(\w+)\s+(?:bigger|smaller|larger|tinier)/i, // "header bigger"
    ],
    action: 'findMatchingComponents',
  },

  // Relative value patterns - need specific values
  relativeValue: {
    patterns: [
      /\b(?:bigger|larger)\b/i,
      /\b(?:smaller|tinier)\b/i,
      /\bmore\s+\w+/i,          // "more padding", "more transparent"
      /\bless\s+\w+/i,          // "less margin", "less opacity"
      /\ba\s+(?:bit|little|lot)\s+/i,  // "a bit larger", "a lot smaller"
      /\b(?:slightly|much)\s+/i, // "slightly bigger", "much smaller"
    ],
    action: 'suggestValueOptions',
  },

  // Breakpoint ambiguity patterns
  breakpointAmbiguous: {
    patterns: [
      /\bon\s+(?:small|large)\s+screens?\b/i,  // "on small screens"
      /\b(?:mobile|tablet|desktop)\s+only\b/i, // "mobile only" - might need confirmation
      /\bresponsive\b/i,                        // "make it responsive"
      /\bfor\s+(?:phones?|tablets?|laptops?)\b/i,
    ],
    action: 'clarifyBreakpoint',
  },

  // Property ambiguity - what to modify
  propertyAmbiguous: {
    patterns: [
      /\bmake\s+it\s+(?:bigger|smaller)\b/i,  // Ambiguous for non-text components
      /\bincrease\b(?!\s+\w+\s+by)/i,         // "increase" without specifying what
      /\bdecrease\b(?!\s+\w+\s+by)/i,
    ],
    action: 'clarifyProperty',
  },
};

/**
 * Value suggestions for different property types
 */
const VALUE_SUGGESTIONS: Record<string, ValueSuggestion[]> = {
  fontSize: [
    { label: '+4px', value: 4, description: 'Slightly larger', isRelative: true },
    { label: '+8px', value: 8, description: 'Noticeably larger', isRelative: true },
    { label: 'Double', value: 2, description: 'Twice the size', multiplier: 2 },
    { label: 'Custom', value: 'custom', description: 'Enter a specific value' },
  ],
  size: [
    { label: '25% larger', value: 1.25, multiplier: 1.25 },
    { label: '50% larger', value: 1.5, multiplier: 1.5 },
    { label: 'Double', value: 2, multiplier: 2 },
    { label: 'Custom', value: 'custom', description: 'Enter specific dimensions' },
  ],
  spacing: [
    { label: '+8px', value: 8, isRelative: true },
    { label: '+16px', value: 16, isRelative: true },
    { label: '+24px', value: 24, isRelative: true },
    { label: 'Custom', value: 'custom' },
  ],
  opacity: [
    { label: '75%', value: 0.75 },
    { label: '50%', value: 0.5 },
    { label: '25%', value: 0.25 },
    { label: 'Custom', value: 'custom' },
  ],
};

/**
 * Breakpoint options for clarification
 */
const BREAKPOINT_OPTIONS: ClarificationOption[] = [
  { id: 'mobile', label: 'Mobile', description: 'Screens smaller than 600px' },
  { id: 'tablet', label: 'Tablet', description: 'Screens 600px - 1023px' },
  { id: 'desktop', label: 'Desktop', description: 'Screens 1024px and larger', isRecommended: true },
  { id: 'all', label: 'All Breakpoints', description: 'Apply to all screen sizes' },
];


// ============================================================================
// Phase 4.1.6: Chart-Specific Clarification Options
// ============================================================================

/**
 * Chart theme options for clarification
 */
const CHART_THEME_OPTIONS: ClarificationOption[] = [
  { id: 'agricultural', label: 'Agricultural', description: 'Natural greens for farm/agriculture', isRecommended: true },
  { id: 'blue', label: 'Blue', description: 'Professional blue tones' },
  { id: 'rainbow', label: 'Rainbow', description: 'Colorful, vibrant palette' },
  { id: 'sunset', label: 'Sunset', description: 'Warm red/orange/yellow tones' },
  { id: 'pastel', label: 'Pastel', description: 'Soft, muted colors' },
  { id: 'forest', label: 'Forest', description: 'Deep natural greens' },
  { id: 'ocean', label: 'Ocean', description: 'Blue/teal water tones' },
  { id: 'corporate', label: 'Corporate', description: 'Professional gray tones' },
];

/**
 * Chart type conversion options for clarification
 */
const CHART_TYPE_OPTIONS: ClarificationOption[] = [
  { id: 'line-chart', label: 'Line Chart', description: 'Show trends over time' },
  { id: 'bar-chart', label: 'Bar Chart', description: 'Compare values across categories', isRecommended: true },
  { id: 'pie-chart', label: 'Pie Chart', description: 'Show proportions of a whole' },
  { id: 'area-chart', label: 'Area Chart', description: 'Show cumulative trends' },
  { id: 'scatter-chart', label: 'Scatter Plot', description: 'Show correlation between variables' },
];

/**
 * Chart ambiguity patterns - Phase 4.1.6
 */
const CHART_AMBIGUITY_PATTERNS = {
  // Theme ambiguity - "use theme" without specifying which
  chartTheme: {
    patterns: [
      /\b(?:use|apply|set)\s+(?:a\s+)?theme\b/i,
      /\b(?:change|switch)\s+(?:the\s+)?theme\b/i,
      /\bcolou?r\s+scheme\b/i,
      /\buse\s+(?:different|another|new)\s+colou?rs?\b/i,
    ],
    action: 'clarifyChartTheme',
  },

  // Chart type conversion without target - "convert chart" without specifying to what
  chartConversion: {
    patterns: [
      /\bconvert\s+(?:the\s+)?chart\b(?!\s+to)/i,
      /\bchange\s+chart\s+type\b/i,
      /\bswitch\s+(?:the\s+)?chart\b(?!\s+to)/i,
      /\btransform\s+(?:the\s+)?chart\b(?!\s+to)/i,
    ],
    action: 'clarifyChartType',
  },

  // Series ambiguity - which series to modify
  chartSeries: {
    patterns: [
      /\b(?:modify|change|update|edit)\s+(?:the\s+)?series\b(?!\s+["'])/i,
      /\bwhich\s+series\b/i,
      /\b(?:hide|show)\s+(?:a\s+)?series\b(?!\s+["'])/i,
    ],
    action: 'clarifyChartSeries',
  },
};

/**
 * ClarificationService class
 */
class ClarificationService {
  private static instance: ClarificationService;
  private config: ClarificationServiceConfig;
  private pendingClarifications: Map<string, ClarificationRequest> = new Map();

  private constructor(config: Partial<ClarificationServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    logger.debug("[ClarificationService] Initialized with config", this.config);
  }

  static getInstance(config?: Partial<ClarificationServiceConfig>): ClarificationService {
    if (!ClarificationService.instance) {
      ClarificationService.instance = new ClarificationService(config);
    }
    return ClarificationService.instance;
  }

  /**
   * Main entry point - detect ambiguity in a command
   */
  detectAmbiguity(
    command: string,
    targetComponent?: Component | null,
    allComponents?: Component[]
  ): AmbiguityDetectionResult {
    console.log('[ClarificationService] Detecting ambiguity for:', command);

    const components = allComponents || useEditorStore.getState().components;
    const normalizedCommand = command.toLowerCase().trim();

    // 1. Check for component reference ambiguity (only if no target component selected)
    if (!targetComponent && this.config.enableComponentMatching) {
      const componentResult = this.detectComponentAmbiguity(normalizedCommand, components);
      if (componentResult.isAmbiguous) {
        console.log('[ClarificationService] Component ambiguity detected:', componentResult);
        return componentResult;
      }
    }

    // 2. Check for relative value ambiguity
    if (this.config.enableValueSuggestions) {
      const valueResult = this.detectValueAmbiguity(normalizedCommand, targetComponent);
      if (valueResult.isAmbiguous) {
        console.log('[ClarificationService] Value ambiguity detected:', valueResult);
        return valueResult;
      }
    }

    // 3. Check for breakpoint ambiguity
    if (this.config.enableBreakpointClarification) {
      const breakpointResult = this.detectBreakpointAmbiguity(normalizedCommand);
      if (breakpointResult.isAmbiguous) {
        console.log('[ClarificationService] Breakpoint ambiguity detected:', breakpointResult);
        return breakpointResult;
      }
    }

    // 4. Phase 4.1.6: Check for chart-specific ambiguity
    if (targetComponent && chartTypeRegistry.isChartType(targetComponent.type)) {
      const chartResult = this.detectChartAmbiguity(normalizedCommand, targetComponent);
      if (chartResult.isAmbiguous) {
        console.log('[Phase 4.1] Chart ambiguity detected:', chartResult);
        return chartResult;
      }
    }

    // No ambiguity detected
    console.log('[ClarificationService] No ambiguity detected');
    return { isAmbiguous: false, confidence: 1.0 };
  }

  /**
   * Detect component reference ambiguity
   */
  private detectComponentAmbiguity(
    command: string,
    components: Component[]
  ): AmbiguityDetectionResult {
    // Extract component references from command
    const references = componentReferenceResolver.extractComponentReferences(command);

    if (references.length === 0) {
      return { isAmbiguous: false, confidence: 1.0 };
    }

    // Try to resolve each reference
    for (const reference of references) {
      const suggestions = componentReferenceResolver.getSuggestions(reference, components);

      // If multiple components match with similar confidence, it's ambiguous
      if (suggestions.length > 1) {
        const matchedComponents: MatchedComponent[] = suggestions
          .slice(0, this.config.maxOptionsToShow)
          .map(comp => {
            const props: BaseComponentProperties = comp.properties;

            return {
              id: comp.id,
              name: comp.name || comp.type,
              type: comp.type,
              confidence: this.calculateMatchConfidence(reference, comp),
              matchReason: this.getMatchReason(reference, comp),
              semanticRole: props._semanticRole,
            };
          });

        // Generate options for the user
        const suggestedOptions: ClarificationOption[] = matchedComponents.map(comp => ({
          id: comp.id,
          label: comp.name || comp.type,
          description: comp.semanticRole || `${comp.type} component`,
          metadata: { componentId: comp.id, confidence: comp.confidence },
        }));

        return {
          isAmbiguous: true,
          type: 'component_selection',
          reason: `Multiple components match "${reference}"`,
          matchedComponents,
          suggestedOptions,
          confidence: 0.5,
        };
      }
    }

    return { isAmbiguous: false, confidence: 1.0 };
  }

  /**
   * Detect relative value ambiguity
   */
  private detectValueAmbiguity(
    command: string,
    targetComponent?: Component | null
  ): AmbiguityDetectionResult {
    // Check for relative value patterns
    for (const pattern of AMBIGUITY_PATTERNS.relativeValue.patterns) {
      if (pattern.test(command)) {
        // Determine what property is being modified
        const propertyType = this.inferPropertyType(command, targetComponent);
        const suggestions = VALUE_SUGGESTIONS[propertyType] || VALUE_SUGGESTIONS.size;

        const suggestedOptions: ClarificationOption[] = suggestions.map((sugg, index) => ({
          id: `value_${index}`,
          label: sugg.label,
          description: sugg.description,
          isRecommended: index === 1, // Second option is usually a good default
          preview: this.generateValuePreview(sugg, targetComponent),
          metadata: { value: sugg.value, isRelative: sugg.isRelative, multiplier: sugg.multiplier },
        }));

        return {
          isAmbiguous: true,
          type: 'value_selection',
          reason: 'Relative value needs clarification',
          suggestedOptions,
          confidence: 0.6,
        };
      }
    }

    return { isAmbiguous: false, confidence: 1.0 };
  }

  /**
   * Detect breakpoint ambiguity
   */
  private detectBreakpointAmbiguity(command: string): AmbiguityDetectionResult {
    for (const pattern of AMBIGUITY_PATTERNS.breakpointAmbiguous.patterns) {
      if (pattern.test(command)) {
        // Check if "small screens" or "large screens" is used
        if (/small\s+screens?/i.test(command) || /large\s+screens?/i.test(command)) {
          return {
            isAmbiguous: true,
            type: 'breakpoint_selection',
            reason: 'Breakpoint reference is ambiguous',
            suggestedOptions: BREAKPOINT_OPTIONS,
            confidence: 0.7,
          };
        }
      }
    }

    return { isAmbiguous: false, confidence: 1.0 };
  }

  // ============================================================================
  // Phase 4.1.6: Chart-Specific Ambiguity Detection
  // ============================================================================

  /**
   * Detect chart-specific ambiguity in commands
   */
  private detectChartAmbiguity(
    command: string,
    component: Component
  ): AmbiguityDetectionResult {
    console.log('[Phase 4.1] Checking chart ambiguity for:', command);

    // Check for theme ambiguity
    for (const pattern of CHART_AMBIGUITY_PATTERNS.chartTheme.patterns) {
      if (pattern.test(command)) {
        // Make sure they didn't specify a theme name
        const themeNames = ['agricultural', 'blue', 'rainbow', 'sunset', 'pastel', 'forest', 'ocean', 'corporate', 'red', 'green', 'purple', 'grayscale', 'vibrant'];
        const specifiedTheme = themeNames.some(theme => command.toLowerCase().includes(theme));

        if (!specifiedTheme) {
          console.log('[Phase 4.1] Chart theme ambiguity detected');
          return {
            isAmbiguous: true,
            type: 'value_selection',
            reason: 'Which color theme would you like to use?',
            suggestedOptions: CHART_THEME_OPTIONS.slice(0, this.config.maxOptionsToShow),
            confidence: 0.7,
          };
        }
      }
    }

    // Check for chart type conversion ambiguity
    for (const pattern of CHART_AMBIGUITY_PATTERNS.chartConversion.patterns) {
      if (pattern.test(command)) {
        console.log('[Phase 4.1] Chart type conversion ambiguity detected');

        // Filter out the current chart type from options
        const filteredOptions = CHART_TYPE_OPTIONS.filter(
          opt => opt.id !== component.type
        );

        return {
          isAmbiguous: true,
          type: 'value_selection',
          reason: 'Which chart type would you like to convert to?',
          suggestedOptions: filteredOptions.slice(0, this.config.maxOptionsToShow),
          confidence: 0.7,
        };
      }
    }

    // Check for series ambiguity
    for (const pattern of CHART_AMBIGUITY_PATTERNS.chartSeries.patterns) {
      if (pattern.test(command)) {
        // Get available series from component data
        const seriesOptions = this.getSeriesOptions(component);

        if (seriesOptions.length > 1) {
          console.log('[Phase 4.1] Chart series ambiguity detected');
          return {
            isAmbiguous: true,
            type: 'value_selection',
            reason: 'Which series would you like to modify?',
            suggestedOptions: seriesOptions.slice(0, this.config.maxOptionsToShow),
            confidence: 0.7,
          };
        }
      }
    }

    return { isAmbiguous: false, confidence: 1.0 };
  }

  /**
   * Extract series names from component data for clarification options
   */
  private getSeriesOptions(component: Component): ClarificationOption[] {
    const data = component.properties?.data;
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Get unique series keys from data (excluding 'name' and 'label')
    const firstPoint = data[0] as Record<string, unknown>;
    const seriesKeys = Object.keys(firstPoint).filter(
      key => !['name', 'label', 'id', 'category'].includes(key.toLowerCase()) &&
             typeof firstPoint[key] === 'number'
    );

    // Also check for lineKeys or series property
    const configuredSeries = component.properties?.lineKeys ||
                            component.properties?.series ||
                            component.properties?.dataKeys;

    let options: ClarificationOption[] = [];

    if (Array.isArray(configuredSeries)) {
      options = configuredSeries.map((series: string, index: number) => ({
        id: series,
        label: series.charAt(0).toUpperCase() + series.slice(1),
        description: `Data series ${index + 1}`,
        isRecommended: index === 0,
      }));
    } else if (seriesKeys.length > 0) {
      options = seriesKeys.map((key, index) => ({
        id: key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        description: `Data column: ${key}`,
        isRecommended: index === 0,
      }));
    }

    // Add "All series" option
    if (options.length > 1) {
      options.push({
        id: 'all',
        label: 'All Series',
        description: 'Apply to all data series',
      });
    }

    return options;
  }

  /**
   * Generate a clarification request from detection result
   */
  generateClarificationRequest(
    result: AmbiguityDetectionResult,
    originalCommand: string,
    targetComponent?: Component | null
  ): ClarificationRequest | null {
    if (!result.isAmbiguous || !result.type) {
      return null;
    }

    const requestId = uuidv4();
    let question: string;
    let allowCustomInput = false;
    let customInputPlaceholder: string | undefined;

    switch (result.type) {
      case 'component_selection':
        question = `I found ${result.matchedComponents?.length || 'multiple'} components that could match your request. Which one did you mean?`;
        break;

      case 'value_selection':
        question = 'How much would you like to change it?';
        allowCustomInput = true;
        customInputPlaceholder = 'Enter a custom value (e.g., 24px, 50%)';
        break;

      case 'breakpoint_selection':
        question = 'Which screen size should this apply to?';
        break;

      case 'property_selection':
        question = 'What would you like to change?';
        break;

      case 'confirmation':
        question = 'Are you sure you want to make this change?';
        break;

      default:
        return null;
    }

    const request: ClarificationRequest = {
      id: requestId,
      type: result.type,
      question,
      options: result.suggestedOptions || [],
      allowCustomInput,
      customInputPlaceholder,
      context: {
        originalCommand,
        detectedIntent: result.reason || '',
        targetComponentId: targetComponent?.id,
        targetComponentType: targetComponent?.type,
        matchedComponents: result.matchedComponents,
      },
      createdAt: new Date(),
    };

    // Store pending clarification
    this.pendingClarifications.set(requestId, request);
    console.log('[ClarificationService] Generated clarification request:', request);

    return request;
  }

  /**
   * Process a clarification response and return the resolved command context
   */
  processClarificationResponse(
    response: ClarificationResponse
  ): {
    resolvedComponentId?: string;
    resolvedValue?: number | string;
    resolvedBreakpoint?: string;
    modifiedCommand?: string;
  } | null {
    const request = this.pendingClarifications.get(response.requestId);
    if (!request) {
      console.warn('[ClarificationService] No pending clarification found for:', response.requestId);
      return null;
    }

    console.log('[ClarificationService] Processing response:', response);

    const selectedOption = request.options.find(opt => opt.id === response.selectedOptionId);
    let result: Record<string, unknown> = {};

    switch (request.type) {
      case 'component_selection':
        result.resolvedComponentId = response.selectedOptionId;
        break;

      case 'value_selection':
        if (response.customValue) {
          result.resolvedValue = response.customValue;
        } else if (selectedOption?.metadata) {
          result.resolvedValue = selectedOption.metadata.value;
        }
        break;

      case 'breakpoint_selection':
        result.resolvedBreakpoint = response.selectedOptionId;
        break;
    }

    // Clean up pending clarification
    this.pendingClarifications.delete(response.requestId);

    return result;
  }

  /**
   * Check if a command is a correction ("I meant...")
   */
  isCorrection(command: string): boolean {
    const correctionPatterns = [
      /^i\s+meant/i,
      /^actually,?\s+i\s+meant/i,
      /^no,?\s+i\s+meant/i,
      /^sorry,?\s+i\s+meant/i,
      /^not\s+that\s+one/i,
      /^the\s+other\s+one/i,
      /^undo/i,
    ];

    return correctionPatterns.some(pattern => pattern.test(command.trim()));
  }

  /**
   * Parse a correction command to extract the intended target
   */
  parseCorrection(command: string): { type: 'undo' | 'retarget' | 'revalue'; value?: string } {
    if (/^undo/i.test(command)) {
      return { type: 'undo' };
    }

    const retargetMatch = command.match(/meant\s+(?:the\s+)?(.+)/i);
    if (retargetMatch) {
      return { type: 'retarget', value: retargetMatch[1].trim() };
    }

    return { type: 'undo' };
  }

  /**
   * Get pending clarification by ID
   */
  getPendingClarification(requestId: string): ClarificationRequest | undefined {
    return this.pendingClarifications.get(requestId);
  }

  /**
   * Check if there's a pending clarification
   */
  hasPendingClarification(): boolean {
    return this.pendingClarifications.size > 0;
  }

  /**
   * Clear all pending clarifications
   */
  clearPendingClarifications(): void {
    this.pendingClarifications.clear();
    console.log('[ClarificationService] Cleared all pending clarifications');
  }

  /**
   * Register an external clarification request (e.g., from semantic resolution)
   * This allows Phase 2.1 semantic resolution to use the same clarification processing
   */
  registerClarification(request: ClarificationRequest): void {
    this.pendingClarifications.set(request.id, request);
    console.log('[ClarificationService] Registered external clarification:', request.id);
  }

  // ============================================================================
  // Helper methods
  // ============================================================================

  private calculateMatchConfidence(reference: string, component: Component): number {
    const props: BaseComponentProperties = component.properties;
    const name = component.name?.toLowerCase() || '';
    const type = component.type.toLowerCase();
    const semanticRole = (props._semanticRole ?? '').toLowerCase();

    // Exact name match
    if (name === reference.toLowerCase()) return 1.0;

    // Semantic role match
    if (semanticRole.includes(reference.toLowerCase())) return 0.9;

    // Partial name match
    if (name.includes(reference.toLowerCase())) return 0.8;

    // Type match
    if (type.includes(reference.toLowerCase())) return 0.7;

    return 0.5;
  }

  private getMatchReason(reference: string, component: Component): string {
    const props: BaseComponentProperties = component.properties;
    const name = component.name?.toLowerCase() || '';
    const type = component.type.toLowerCase();
    const semanticRole = (props._semanticRole ?? '').toLowerCase();

    if (name === reference.toLowerCase()) return 'Exact name match';
    if (semanticRole.includes(reference.toLowerCase())) return 'Semantic role match';
    if (name.includes(reference.toLowerCase())) return 'Partial name match';
    if (type.includes(reference.toLowerCase())) return 'Component type match';
    return 'Similar component';
  }

  private inferPropertyType(command: string, component?: Component | null): string {
    const lowerCommand = command.toLowerCase();

    // Text-related
    if (component?.type === ComponentType.TEXT || /\b(?:text|font|letter)\b/.test(lowerCommand)) {
      return 'fontSize';
    }

    // Spacing-related
    if (/\b(?:padding|margin|gap|spacing)\b/.test(lowerCommand)) {
      return 'spacing';
    }

    // Opacity-related
    if (/\b(?:opacity|transparent|fade)\b/.test(lowerCommand)) {
      return 'opacity';
    }

    // Default to size
    return 'size';
  }

  private generateValuePreview(
    suggestion: ValueSuggestion,
    component?: Component | null
  ): PropertyChangePreview | undefined {
    if (!component || suggestion.value === 'custom') {
      return undefined;
    }

    const width = typeof component.size.width === 'number'
      ? component.size.width
      : Number.parseFloat(String(component.size.width)) || 0;
    const height = typeof component.size.height === 'number'
      ? component.size.height
      : Number.parseFloat(String(component.size.height)) || 0;
    const preview: PropertyChangePreview = {};

    if (suggestion.multiplier) {
      preview.size = {
        width: Math.round(width * suggestion.multiplier),
        height: Math.round(height * suggestion.multiplier),
      };
    } else if (suggestion.isRelative && typeof suggestion.value === 'number') {
      if (component.type === ComponentType.TEXT) {
        const rawFontSize = (component.properties as any)?.fontSize;
        const currentSize = typeof rawFontSize === 'number'
          ? rawFontSize
          : Number.parseFloat(String(rawFontSize)) || 16;
        preview.properties = { fontSize: currentSize + suggestion.value };
      } else {
        preview.size = {
          width: width + suggestion.value,
          height: height + suggestion.value,
        };
      }
    }

    return preview;
  }
}

// Export singleton instance
export const clarificationService = ClarificationService.getInstance();

// Export class for testing
export { ClarificationService };
